import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Check, Mailbox, Search, X } from 'lucide-react';
import {
  fetchVersandprotokoll,
  type MailAnlass,
  type VersandEintrag,
} from '@/api/versandprotokoll.api';

// ─── Versandprotokoll ────────────────────────────────────────────────────────
//
// Wer hier hereinschaut, hat fast immer eine konkrete Frage: „Ist die
// Einladung an diese Adresse rausgegangen?" oder „Warum hat der Kunde die
// Passwort-Mail nicht bekommen?"
//
// Deshalb steht die Suche nach dem Empfänger oben, der Fehlschlag ist so
// sichtbar wie der Erfolg, und die Fehlermeldung des Mailservers steht im
// Klartext dabei – „fehlgeschlagen" allein sagt niemandem, ob der Server
// klemmt oder die Adresse tot ist.
//
// Der INHALT der Mails steht nicht hier. Festgehalten werden nur die
// Umstände; Passwort-Links und persönliche Texte dauerhaft aufzubewahren
// wäre ein Risiko ohne Gegenwert.

const ANLASS_LABEL: Record<MailAnlass, string> = {
  einladung: 'Einladung',
  betriebszugriff: 'Betriebszugriff',
  'zweiter-faktor': 'Zwei-Faktor',
  passwort: 'Passwort',
  anmeldung: 'Anmeldehinweis',
  rundnachricht: 'Rundnachricht',
  kalender: 'Kalender-Erinnerung',
  probe: 'Probemail',
  'smtp-test': 'SMTP-Test',
  sonstige: 'Sonstige',
};

const ANLAESSE: MailAnlass[] = [
  'einladung', 'betriebszugriff', 'zweiter-faktor', 'passwort', 'anmeldung',
  'rundnachricht', 'kalender', 'probe', 'smtp-test', 'sonstige',
];

function zeit(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function Zeile({ eintrag }: { eintrag: VersandEintrag }) {
  return (
    <tr className="border-t border-slate-100 align-top">
      <td className="px-3 py-2 whitespace-nowrap text-slate-500">{zeit(eintrag.zeitpunkt)}</td>
      <td className="px-3 py-2">
        {eintrag.erfolg ? (
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <Check className="h-3.5 w-3.5" aria-hidden="true" /> zugestellt
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-red-600">
            <X className="h-3.5 w-3.5" aria-hidden="true" /> gescheitert
          </span>
        )}
      </td>
      <td className="px-3 py-2 break-all text-slate-900">{eintrag.empfaenger}</td>
      <td className="px-3 py-2">
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
          {ANLASS_LABEL[eintrag.anlass] ?? eintrag.anlass}
        </span>
      </td>
      <td className="px-3 py-2 text-slate-700">
        {eintrag.betreff}
        {eintrag.fehler && (
          <p className="mt-1 font-mono text-[11px] leading-snug text-red-600">{eintrag.fehler}</p>
        )}
      </td>
    </tr>
  );
}

export function VersandprotokollPage() {
  const [suche, setSuche] = useState('');
  const [anlass, setAnlass] = useState('');
  const [nurFehler, setNurFehler] = useState(false);
  const [seite, setSeite] = useState(0);

  const filter = { empfaenger: suche.trim(), anlass, nurFehler, seite };
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'versandprotokoll', filter],
    queryFn: () => fetchVersandprotokoll(filter),
  });

  const seiten = data ? Math.ceil(data.gesamt / data.proSeite) : 0;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900 sm:text-2xl">
          <Mailbox className="h-5 w-5 text-slate-400" aria-hidden="true" />
          Versandprotokoll
        </h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Jede E-Mail, die diese Installation verschickt hat – mit Empfänger, Anlass und
          Ergebnis. Der Inhalt steht bewusst nicht dabei.
        </p>
      </header>

      <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[16rem] flex-1">
            <label htmlFor="suche" className="block text-xs font-medium text-slate-700">
              Empfänger
            </label>
            <div className="relative mt-1">
              <Search
                className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400"
                aria-hidden="true"
              />
              <input
                id="suche"
                value={suche}
                onChange={(e) => { setSuche(e.target.value); setSeite(0); }}
                placeholder="Teil der Adresse genügt"
                className="w-full rounded-md border border-slate-300 py-2 pl-8 pr-3 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </div>
          </div>

          <div>
            <label htmlFor="anlass" className="block text-xs font-medium text-slate-700">
              Anlass
            </label>
            <select
              id="anlass"
              value={anlass}
              onChange={(e) => { setAnlass(e.target.value); setSeite(0); }}
              className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              <option value="">Alle Anlässe</option>
              {ANLAESSE.map((a) => (
                <option key={a} value={a}>{ANLASS_LABEL[a]}</option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={nurFehler}
              onChange={(e) => { setNurFehler(e.target.checked); setSeite(0); }}
              className="rounded border-slate-300"
            />
            Nur Fehlschläge
          </label>
        </div>

        {data && data.fehlerZahl > 0 && !nurFehler && (
          <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              {data.fehlerZahl} {data.fehlerZahl === 1 ? 'Versand ist' : 'Versände sind'}{' '}
              gescheitert.{' '}
              <button onClick={() => { setNurFehler(true); setSeite(0); }} className="underline">
                Nur diese zeigen
              </button>
            </span>
          </p>
        )}
      </section>

      {isLoading && <p className="py-16 text-center text-sm text-slate-400">Wird geladen …</p>}
      {isError && (
        <p className="py-16 text-center text-sm text-red-500">
          Das Protokoll konnte nicht geladen werden.
        </p>
      )}

      {data && data.eintraege.length === 0 && (
        <p className="rounded-lg border border-dashed border-slate-200 px-4 py-16 text-center text-sm text-slate-400">
          {data.gesamt === 0 && !suche && !anlass && !nurFehler
            ? 'Es wurde noch keine E-Mail verschickt.'
            : 'Unter diesen Bedingungen steht nichts.'}
        </p>
      )}

      {data && data.eintraege.length > 0 && (
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Zeitpunkt</th>
                  <th className="px-3 py-2 font-medium">Ergebnis</th>
                  <th className="px-3 py-2 font-medium">Empfänger</th>
                  <th className="px-3 py-2 font-medium">Anlass</th>
                  <th className="px-3 py-2 font-medium">Betreff</th>
                </tr>
              </thead>
              <tbody>
                {data.eintraege.map((e) => <Zeile key={e.id} eintrag={e} />)}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2 text-xs text-slate-500">
            <span>
              {data.gesamt} {data.gesamt === 1 ? 'Eintrag' : 'Einträge'}
              {seiten > 1 && ` · Seite ${seite + 1} von ${seiten}`}
            </span>
            {seiten > 1 && (
              <span className="flex gap-2">
                <button
                  onClick={() => setSeite((s) => Math.max(0, s - 1))}
                  disabled={seite === 0}
                  className="rounded border border-slate-200 px-2 py-1 disabled:opacity-40"
                >
                  Zurück
                </button>
                <button
                  onClick={() => setSeite((s) => s + 1)}
                  disabled={seite + 1 >= seiten}
                  className="rounded border border-slate-200 px-2 py-1 disabled:opacity-40"
                >
                  Weiter
                </button>
              </span>
            )}
          </div>
        </section>
      )}

      <p className="text-xs text-slate-400">
        Die Einträge werden nach derselben Frist gelöscht wie das übrige Protokoll –
        einzustellen unter <strong>Einstellungen</strong>.
      </p>
    </div>
  );
}
